import { useEffect, useState } from "react";
import { api, toast } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TargetPicker } from "./TargetPicker";
import DatePicker from "@/components/DatePicker";

export const CALL_OUTCOMES = [
  { value: "success", label: "Successful" },
  { value: "follow_up", label: "Follow-up Needed" },
  { value: "no_answer", label: "No Answer" },
];
const CHIPS = ["Voice message", "Not connected", "Email sent", "Meeting scheduled"];
const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const blank = () => ({ call_at: toLocalInput(new Date()), outcome: "success", duration_min: "", contact_id: "none", notes: "", follow_up_date: "" });

// preset = { entity_type, entity_id, name } to lock the target; omit for Quick Log Call
export const LogCallDialog = ({ open, onClose, preset, onLogged }) => {
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(blank());
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) { setForm(blank()); setTarget(preset || null); }
  }, [open, preset?.entity_id]);

  useEffect(() => {
    setContacts([]);
    set("contact_id", "none");
    if (!target) return;
    api.get(`/contacts?${target.entity_type}_id=${target.entity_id}`)
      .then((r) => setContacts(r.data.filter((c) => !c.is_left)))
      .catch(() => {});
  }, [target]);

  const now = new Date();
  const min = toLocalInput(new Date(now.getTime() - 7 * 864e5));
  const addChip = (c) => set("notes", form.notes ? `${form.notes.trimEnd()} ${c}.` : `${c}.`);

  const submit = async () => {
    if (!target) return toast.error("Select a client or lead");
    if (!form.notes.trim()) return toast.error("Notes are required");
    if (form.outcome === "follow_up" && !form.follow_up_date) return toast.error("Pick a follow-up date");
    setSaving(true);
    try {
      const res = await api.post("/call-logs", {
        entity_type: target.entity_type,
        entity_id: target.entity_id,
        call_at: new Date(form.call_at).toISOString(),
        outcome: form.outcome,
        duration_min: form.duration_min === "" ? null : Number(form.duration_min),
        contact_id: form.contact_id === "none" ? null : form.contact_id,
        notes: form.notes,
        follow_up_date: form.outcome === "follow_up" ? form.follow_up_date : null,
      });
      toast.success(`Call logged for ${target.name}`);
      onLogged?.(res.data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to log call");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-white" data-testid="log-call-dialog">
        <DialogHeader><DialogTitle>{preset ? `Log Call — ${preset.name}` : "Quick Log Call"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!preset && (
            <div className="space-y-2">
              <Label>Client / Lead <span className="text-red-500">*</span></Label>
              <TargetPicker value={target} onChange={setTarget} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="call-at">Call Date & Time <span className="text-red-500">*</span></Label>
              <Input id="call-at" type="datetime-local" min={min} max={toLocalInput(now)} value={form.call_at} onChange={(e) => set("call_at", e.target.value)} data-testid="log-call-datetime" />
              <p className="text-[11px] text-gray-500">Up to 7 days back</p>
            </div>
            <div className="space-y-2">
              <Label>Spoken To</Label>
              <Select value={form.contact_id} onValueChange={(v) => set("contact_id", v)} disabled={!target}>
                <SelectTrigger data-testid="log-call-contact"><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.designation ? ` (${c.designation})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Outcome <span className="text-red-500">*</span></Label>
              <Select value={form.outcome} onValueChange={(v) => set("outcome", v)}>
                <SelectTrigger data-testid="log-call-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{CALL_OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="call-duration">Duration (optional)</Label>
              <Input id="call-duration" type="number" min={0} max={600} placeholder="Minutes" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} data-testid="log-call-duration" />
            </div>
          </div>
          {form.outcome === "follow_up" && (
            <div className="space-y-2 rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="log-call-followup-section">
              <Label>Follow-up Date <span className="text-red-500">*</span></Label>
              <DatePicker value={form.follow_up_date} min={toLocalInput(now).slice(0, 10)} onChange={(e) => set("follow_up_date", e.target.value)} data-testid="log-call-followup-date" />
              <p className="text-[11px] text-amber-800">You'll see this under My Follow-Ups on My Workspace on that day.</p>
            </div>
          )}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor="call-notes">Notes <span className="text-red-500">*</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {CHIPS.map((c) => (
                  <button key={c} type="button" onClick={() => addChip(c)} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors" data-testid={`log-call-chip-${c.toLowerCase().replace(/ /g, "-")}`}>{c}</button>
                ))}
              </div>
            </div>
            <Textarea id="call-notes" rows={4} maxLength={1000} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Call notes and details..." data-testid="log-call-notes" />
            <p className="text-[11px] text-gray-500 text-right">{form.notes.length}/1000</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="log-call-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-md3-primary text-md3-on-primary rounded-md3-full" data-testid="log-call-submit-btn">Log Call</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
